import { useEffect, useState } from "react";
import { Document, Page } from "react-pdf";
import { Button, Stack, Typography } from "@financeable-com-au/financeable-ui";

interface PDFPreviewerProps {
  s3Url: string;
}

interface PDFPreviewerState {
  numPages: number | null;
}

const PDFPreviewer = ({ s3Url }: PDFPreviewerProps) => {
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [pdfBuffer, setPdfBuffer] = useState<null>(null);

  useEffect(() => {
    const fetchPdf = async () => {
      try {
        const response = await fetch(s3Url);
        if (!response.ok) throw new Error(`Failed to fetch PDF: ${response.status}`);
        const blob = await response.blob();
        const objectUrl = URL.createObjectURL(blob);
        setPdfBuffer(objectUrl as unknown as null);
      } catch (error) {
        console.error("Failed to fetch PDF:", error);
      }
    };

    fetchPdf();

    // Cleanup object URL on unmount
    return () => {
      if (pdfBuffer) {
        URL.revokeObjectURL(pdfBuffer);
      }
    };
  }, [s3Url]);

  const onDocumentLoadSuccess = ({ numPages }: PDFPreviewerState) => {
    setNumPages(numPages);
  };

  const goToPrevPage = () => setPageNumber(pageNumber - 1 <= 1 ? 1 : pageNumber - 1);

  const goToNextPage = () => setPageNumber(pageNumber + 1 >= (numPages ?? 1) ? (numPages ?? 1) : pageNumber + 1);

  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      height="100%"
      sx={{
        p: 2,
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      {pdfBuffer && (
        <Document
          file={pdfBuffer}
          onLoadSuccess={onDocumentLoadSuccess}
          loading={<p>Loading PDF...</p>}
          error={<p>Failed to load PDF. Check S3 URL & CORS settings.</p>}
        >
          <Page
            pageNumber={pageNumber}
            renderTextLayer={false}
            renderAnnotationLayer={false}
            width={window.innerWidth < 600 ? window.innerWidth - 40 : 600}
          />
        </Document>
      )}

      <Stack
        direction="column"
        spacing={2}
        alignItems="center"
        justifyContent="space-between"
        mt={2}
        sx={{ width: "100%" }}
      >
        <Typography level="body-sm">
          Page {pageNumber} of {numPages}
        </Typography>

        <Stack direction={"row"} spacing={2} alignItems="center">
          <Button onClick={goToPrevPage} fullWidth>
            Prev
          </Button>
          <Button onClick={goToNextPage} fullWidth>
            Next
          </Button>
        </Stack>
      </Stack>
    </Stack>
  );
};

export default PDFPreviewer;
